import { useEffect, useMemo, useState } from "react";
import { Building2, UserRound, Save, Trash2, Plus, Clock, CalendarRange, Ban, Lock, Wand2 } from "lucide-react";
import { showError, showSuccess, notifyApiError } from "../../../components/alertas";
import ClientService from "../../../services/client/ClientService";
import logger from "../../../utils/logger";
import "./horarios.css";

const daysOfWeek = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const shortDay = (d) => d.slice(0, 3);

const createEmptyWorkingHours = () => daysOfWeek.map(() => ({ start: "", end: "" }));

const normalizeWorkingHours = (workingHours) => {
    const baseHours = createEmptyWorkingHours();
    if (!Array.isArray(workingHours)) return baseHours;
    daysOfWeek.forEach((day, index) => {
        const entry = workingHours[index] || workingHours.find((item) => item?.day === day) || {};
        baseHours[index] = { start: entry.start || "", end: entry.end || "" };
    });
    return baseHours;
};

const normalizeSchedule = (source = {}) => ({
    maxAdvancedDays: Math.max(1, Number(source?.maxAdvancedDays) || 15),
    workingHours: normalizeWorkingHours(source?.workingHours),
    disabledDays: Array.isArray(source?.disabledDays) ? [...source.disabledDays] : [],
    disabledHours: Array.isArray(source?.disabledHours)
        ? source.disabledHours.filter((item) => item?.day).map((item) => ({ day: item.day, start: item.start || "", end: item.end || "" }))
        : [],
});

const to12Hour = (hhmm) => {
    if (!hhmm) return "—";
    const [h, m] = hhmm.split(":").map(Number);
    return `${((h + 11) % 12 + 1)}:${m.toString().padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

const getCurrentStaff = (clientData, userData) => {
    if (!Array.isArray(clientData?.staff) || clientData.staff.length === 0) return null;
    if (userData?.userType === "admin") return clientData.staff[0] || null;
    return clientData.staff.find((staff) => staff.id === userData?.staffId || staff.name === userData?.username) || null;
};

/**
 * Editor de un horario (del negocio o personal): anticipación, semana día por día
 * con interruptor y rango, atajo "mismo horario para varios días" y horas cerradas.
 * `editable=false` es la vista de solo lectura del staff que usa el del negocio.
 */
function ScheduleEditor({ title, schedule, setSchedule, editable, actionLabel, onAction }) {
    const [quickOpen, setQuickOpen] = useState(false);
    const [customDays, setCustomDays] = useState([]);
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");
    const [disabledHourDays, setDisabledHourDays] = useState([]);
    const [disabledHourStart, setDisabledHourStart] = useState("");
    const [disabledHourEnd, setDisabledHourEnd] = useState("");

    useEffect(() => {
        setQuickOpen(false); setCustomDays([]); setCustomStart(""); setCustomEnd("");
        setDisabledHourDays([]); setDisabledHourStart(""); setDisabledHourEnd("");
    }, [title]);

    const update = (fn) => { if (editable) setSchedule((prev) => fn(normalizeSchedule(prev))); };

    const handleTimeChange = (dayIndex, field, value) => update((next) => {
        next.workingHours[dayIndex] = { ...next.workingHours[dayIndex], [field]: value };
        return next;
    });
    const toggleDisabledDay = (day) => update((next) => {
        next.disabledDays = next.disabledDays.includes(day) ? next.disabledDays.filter((d) => d !== day) : [...next.disabledDays, day];
        return next;
    });
    const toggleDisabledHourDay = (day) => setDisabledHourDays((prev) => prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]);
    // Un mismo rango para varios días: se guarda una entrada por día (así lo lee el
    // backend). Si ese día ya tiene exactamente ese rango, no se duplica.
    const addDisabledHour = () => {
        if (!editable || !disabledHourDays.length || !disabledHourStart || !disabledHourEnd) return;
        update((next) => {
            const exists = (day) => next.disabledHours.some((h) => h.day === day && h.start === disabledHourStart && h.end === disabledHourEnd);
            const added = daysOfWeek.filter((day) => disabledHourDays.includes(day) && !exists(day)).map((day) => ({ day, start: disabledHourStart, end: disabledHourEnd }));
            return { ...next, disabledHours: [...next.disabledHours, ...added] };
        });
        setDisabledHourDays([]); setDisabledHourStart(""); setDisabledHourEnd("");
    };
    const removeDisabledHour = (index) => update((next) => ({ ...next, disabledHours: next.disabledHours.filter((_, i) => i !== index) }));
    const applyQuickConfig = () => {
        if (!editable || !customDays.length || !customStart || !customEnd) return;
        update((next) => {
            const updatedHours = [...next.workingHours];
            customDays.forEach((day) => { const i = daysOfWeek.indexOf(day); if (i !== -1) updatedHours[i] = { start: customStart, end: customEnd }; });
            return { ...next, workingHours: updatedHours, disabledDays: next.disabledDays.filter((d) => !customDays.includes(d)) };
        });
        setQuickOpen(false); setCustomDays([]);
    };

    const handleSave = () => {
        const normalized = normalizeSchedule(schedule);
        const workingHoursWithDay = normalized.workingHours.map((wh, index) => ({ day: daysOfWeek[index], start: wh.start || "", end: wh.end || "" }));
        for (const wh of workingHoursWithDay) {
            if (wh.start && wh.end && wh.end <= wh.start) {
                showError("Horario inválido", `La hora de fin debe ser mayor que la de inicio el ${wh.day}. Pusiste ${to12Hour(wh.start)} a ${to12Hour(wh.end)}.`);
                return;
            }
        }
        for (const dh of normalized.disabledHours) {
            if (dh.start && dh.end && dh.end <= dh.start) {
                showError("Horario inválido", `El horario cerrado del ${dh.day} debe terminar después de empezar.`);
                return;
            }
        }
        onAction({ ...normalized, workingHours: workingHoursWithDay });
    };

    const maxAdvancedDays = schedule?.maxAdvancedDays || 15;
    // Mientras se escribe guardamos el texto tal cual (si no, al borrar el "1" el
    // campo lo vuelve a poner y en el cel no se puede corregir). Al salir se ajusta.
    const [advanceDraft, setAdvanceDraft] = useState(null);
    const exampleLimitDate = useMemo(() => new Date(Date.now() + (maxAdvancedDays + 1) * 864e5).toLocaleDateString("es-MX", { day: "numeric", month: "long" }), [maxAdvancedDays]);
    const disabledDays = schedule?.disabledDays || [];
    const activeDays = daysOfWeek.filter((d, i) => !disabledDays.includes(d) && schedule?.workingHours?.[i]?.start && schedule?.workingHours?.[i]?.end).length;

    return (
        <div className="cp-stack cp-sched">
            {!editable && (
                <div className="cp-note"><Lock size={16} /><div>Estás usando el horario del negocio, así que aquí solo se consulta. Para tener el tuyo, elige <b>Mi horario</b>.</div></div>
            )}

            <article className="cp-card" data-tour="horarios.days">
                <div className="cp-card-head">
                    <h3 className="cp-card-title"><CalendarRange size={18} /> Anticipación</h3>
                </div>
                <p className="cp-muted">Con cuántos días de anticipación pueden agendar tus clientes.</p>
                <div className="cp-sched-advance">
                    <div className="cp-sched-advance-input">
                        <input type="number" inputMode="numeric" className="cp-input" min="1" max="365" value={advanceDraft ?? maxAdvancedDays} disabled={!editable}
                            onChange={(e) => {
                                const raw = e.target.value;
                                setAdvanceDraft(raw);
                                const n = parseInt(raw, 10);
                                if (n >= 1) update((next) => ({ ...next, maxAdvancedDays: Math.min(365, n) }));
                            }}
                            onBlur={() => setAdvanceDraft(null)} />
                        <span>días</span>
                    </div>
                    <small className="cp-hint">Hoy podrían agendar hasta el <b>{exampleLimitDate}</b>.</small>
                </div>
            </article>

            <article className="cp-card" data-tour="horarios.week">
                <div className="cp-card-head">
                    <h3 className="cp-card-title"><Clock size={18} /> Semana <span className="cp-count">{activeDays} de 7</span></h3>
                    {editable && (
                        <button type="button" className="cp-linkbtn" onClick={() => setQuickOpen((v) => !v)} aria-expanded={quickOpen}><Wand2 size={15} /> Varios días igual</button>
                    )}
                </div>
                {quickOpen && editable && (
                    <div className="cp-sched-quick">
                        <div className="cp-sched-quick-days">
                            {daysOfWeek.map((day) => (
                                <button key={day} type="button" className={`cp-choice ${customDays.includes(day) ? "is-active" : ""}`} aria-pressed={customDays.includes(day)}
                                    onClick={() => setCustomDays((prev) => prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day])}>{shortDay(day)}</button>
                            ))}
                        </div>
                        <div className="cp-sched-range">
                            <input type="time" className="cp-input" value={customStart} onChange={(e) => setCustomStart(e.target.value)} aria-label="Hora de inicio" />
                            <span>a</span>
                            <input type="time" className="cp-input" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} aria-label="Hora de fin" />
                        </div>
                        <button type="button" className="cp-btn cp-btn-primary" onClick={applyQuickConfig} disabled={!customDays.length || !customStart || !customEnd}>Aplicar a {customDays.length || "los"} días</button>
                    </div>
                )}
                <ul className="cp-sched-week">
                    {daysOfWeek.map((day, index) => {
                        const off = disabledDays.includes(day);
                        const wh = schedule?.workingHours?.[index] || {};
                        return (
                            <li key={day} className={`cp-sched-day ${off ? "is-off" : ""}`}>
                                <div className="cp-sched-day-name">
                                    <button type="button" role="switch" aria-checked={!off} className="cp-toggle" onClick={() => toggleDisabledDay(day)} disabled={!editable} aria-label={`${day} ${off ? "cerrado" : "abierto"}`} />
                                    <span>{day}</span>
                                </div>
                                {off ? (
                                    <span className="cp-sched-closed">Cerrado</span>
                                ) : (
                                    <div className="cp-sched-range">
                                        <input type="time" className="cp-input" value={wh.start || ""} onChange={(e) => handleTimeChange(index, "start", e.target.value)} disabled={!editable} aria-label={`${day}, apertura`} />
                                        <span>a</span>
                                        <input type="time" className="cp-input" value={wh.end || ""} onChange={(e) => handleTimeChange(index, "end", e.target.value)} disabled={!editable} aria-label={`${day}, cierre`} />
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </article>

            <article className="cp-card" data-tour="horarios.disable">
                <div className="cp-card-head">
                    <h3 className="cp-card-title"><Ban size={18} /> Horas cerradas</h3>
                </div>
                <p className="cp-muted">Un rango dentro del día en el que no se agenda: la comida, un descanso fijo.</p>
                <p className="cp-hint cp-sched-closed-hint">La hora de fin queda libre: cerrado de 1:00 a 2:00 p.m. deja disponible el turno de las 2:00 p.m. Si quieres bloquear también ese turno, termina el rango más tarde (2:01 p.m. o el siguiente bloque).</p>
                {schedule?.disabledHours?.length > 0 && (
                    <ul className="cp-list cp-sched-closedlist">
                        {schedule.disabledHours.map((item, index) => (
                            <li key={`${item.day}-${item.start}-${item.end}-${index}`} className="cp-row">
                                <span className="cp-row-main">
                                    <span className="cp-row-title">{item.day}</span>
                                    <span className="cp-row-sub">{to12Hour(item.start)} a {to12Hour(item.end)}</span>
                                </span>
                                {editable && <button type="button" className="cp-iconbtn" onClick={() => removeDisabledHour(index)} aria-label="Quitar"><Trash2 size={16} /></button>}
                            </li>
                        ))}
                    </ul>
                )}
                {editable && (
                    <div className="cp-sched-add">
                        <div className="cp-sched-quick-days" role="group" aria-label="Días">
                            {daysOfWeek.map((day) => (
                                <button key={day} type="button" className={`cp-choice ${disabledHourDays.includes(day) ? "is-active" : ""}`} aria-pressed={disabledHourDays.includes(day)} onClick={() => toggleDisabledHourDay(day)}>{shortDay(day)}</button>
                            ))}
                            <button type="button" className="cp-linkbtn" onClick={() => setDisabledHourDays(disabledHourDays.length === daysOfWeek.length ? [] : [...daysOfWeek])}>
                                {disabledHourDays.length === daysOfWeek.length ? "Ninguno" : "Todos"}
                            </button>
                        </div>
                        <div className="cp-sched-add-row">
                            <div className="cp-sched-range">
                                <input type="time" className="cp-input" value={disabledHourStart} onChange={(e) => setDisabledHourStart(e.target.value)} aria-label="Desde" />
                                <span>a</span>
                                <input type="time" className="cp-input" value={disabledHourEnd} onChange={(e) => setDisabledHourEnd(e.target.value)} aria-label="Hasta" />
                            </div>
                            <button type="button" className="cp-btn" onClick={addDisabledHour} disabled={!disabledHourDays.length || !disabledHourStart || !disabledHourEnd}>
                                <Plus size={16} /> Agregar{disabledHourDays.length > 1 ? ` a ${disabledHourDays.length} días` : ""}
                            </button>
                        </div>
                    </div>
                )}
            </article>

            <div className="cp-sched-save">
                <button type="button" className={`cp-btn ${editable ? "cp-btn-primary" : ""} cp-btn-block`} onClick={handleSave} disabled={!onAction}>
                    {editable ? <Save size={16} /> : <Building2 size={16} />} {actionLabel}
                </button>
            </div>
        </div>
    );
}

const ScheduleTab = ({ schedule, onSaveSchedule, userData, clientData, refetchClientData }) => {
    const isAdmin = userData?.userType === "admin";
    const currentStaff = useMemo(() => getCurrentStaff(clientData, userData), [clientData, userData]);
    const businessScheduleSource = schedule || clientData?.schedule;
    const personalScheduleSource = currentStaff?.schedule || businessScheduleSource;

    const [businessSchedule, setBusinessSchedule] = useState(() => normalizeSchedule(businessScheduleSource));
    const [personalSchedule, setPersonalSchedule] = useState(() => normalizeSchedule(personalScheduleSource));
    const [activeMode, setActiveMode] = useState(isAdmin ? "business" : (currentStaff?.useBusinessSchedule ? "business" : "personal"));
    // Con un solo profesional (el dueño) no tiene sentido "negocio" vs "personal".
    const isSolo = isAdmin && (clientData?.staff?.length || 0) <= 1;

    useEffect(() => { setBusinessSchedule(normalizeSchedule(businessScheduleSource)); }, [businessScheduleSource]);
    useEffect(() => {
        setPersonalSchedule(normalizeSchedule(personalScheduleSource));
        if (isAdmin) setActiveMode((prev) => (prev === "personal" ? "personal" : "business"));
        else setActiveMode(currentStaff?.useBusinessSchedule ? "business" : "personal");
    }, [currentStaff, isAdmin, personalScheduleSource]);

    const handleSavePersonalSchedule = async (scheduleData) => {
        if (!clientData?.id || !currentStaff?.id) { showError("Perfil no encontrado", "No se pudo identificar el profesional actual para guardar su horario."); return; }
        try {
            const response = await ClientService.updateStaffData(clientData.id, currentStaff.id, { schedule: scheduleData, useBusinessSchedule: false });
            if (response) { showSuccess("Horario personal guardado", "Tu horario personal se actualizó correctamente."); await refetchClientData?.(); }
        } catch (error) {
            logger.error("Error saving personal schedule:", error);
            notifyApiError(error, "Guardar horario personal");
        }
    };

    const handleUseBusinessSchedule = async () => {
        if (!clientData?.id || !currentStaff?.id) { showError("Perfil no encontrado", "No se pudo identificar el profesional actual para guardar su preferencia."); return; }
        try {
            await ClientService.updateStaffData(clientData.id, currentStaff.id, { useBusinessSchedule: true });
            showSuccess("Preferencia guardada", "Ahora usas el horario del negocio.");
            await refetchClientData?.();
        } catch (error) {
            logger.error("Error setting business schedule preference:", error);
            notifyApiError(error, "Guardar preferencia de horario");
        }
    };

    if (!clientData) {
        return <div className="cp-stack">{[0, 1, 2].map((i) => <div key={i} className="cp-skeleton" style={{ height: 120 }} />)}</div>;
    }

    const switchToPersonal = () => {
        if (!isAdmin && currentStaff?.schedule) setPersonalSchedule(normalizeSchedule(currentStaff.schedule));
        setActiveMode("personal");
    };
    const personalActive = !isSolo && activeMode === "personal";
    const businessEditable = isAdmin;

    return (
        <section className="cp-schedule">
            <header className="cp-page-head">
                <div>
                    <h2 className="cp-page-title" data-tour="horarios.title">Horarios</h2>
                    <p className="cp-page-sub">Solo se ofrecen citas dentro de estas horas.</p>
                </div>
            </header>

            {!isSolo && (
                <div className="cp-sched-mode">
                    <div className="cp-segment cp-segment-fill" role="tablist" aria-label="Qué horario editar" data-tour="horarios.mode">
                        <button type="button" role="tab" aria-selected={activeMode === "business"} className={`cp-segment-btn ${activeMode === "business" ? "is-active" : ""}`} onClick={() => setActiveMode("business")}><Building2 size={15} /> Del negocio</button>
                        <button type="button" role="tab" aria-selected={activeMode === "personal"} className={`cp-segment-btn ${activeMode === "personal" ? "is-active" : ""}`} onClick={switchToPersonal}><UserRound size={15} /> Mi horario</button>
                    </div>
                    <p className="cp-hint">
                        {isAdmin
                            ? "El del negocio aplica a todos; tu horario personal se guarda aparte en tu perfil de profesional."
                            : "Con el horario del negocio no puedes cambiar nada. Con el tuyo, solo se guardan tus propias horas."}
                    </p>
                </div>
            )}

            {personalActive ? (
                <ScheduleEditor
                    key="personal"
                    title="Mi horario"
                    schedule={personalSchedule}
                    setSchedule={setPersonalSchedule}
                    editable
                    actionLabel="Guardar mi horario"
                    onAction={handleSavePersonalSchedule}
                />
            ) : (
                <ScheduleEditor
                    key="business"
                    title={isSolo ? "Tu horario" : "Horario del negocio"}
                    schedule={businessSchedule}
                    setSchedule={setBusinessSchedule}
                    editable={businessEditable}
                    actionLabel={businessEditable ? (isSolo ? "Guardar horario" : "Guardar horario del negocio") : "Usar horario del negocio"}
                    onAction={businessEditable ? onSaveSchedule : handleUseBusinessSchedule}
                />
            )}
        </section>
    );
};

export default ScheduleTab;
